/**
 * Activity Tracking Service
 * Handles tracking user activity in the application and saving to Firestore
 */

import firebase from "../firebase";
import { v4 as uuidv4 } from "uuid";
import {
        getBrowserInfo,
        getDeviceType,
        getScreenSize,
} from "../utils/deviceInfo";
import brandConfig from "../config/brandConfig";

const OFFLINE_ACTIVITY_KEY = "offlineActivityLog";
const OFFLINE_QUEUE_LIMIT = 200;

export const ActivitySeverity = Object.freeze({
        INFO: "INFO",
        WARN: "WARN",
        ERROR: "ERROR",
        CRITICAL: "CRITICAL",
});

export const ActivityEventTypes = Object.freeze({
        SESSION_START: "SESSION_START",
        SESSION_END: "SESSION_END",
        PAGE_VIEW: "PAGE_VIEW",
        FEATURE_USAGE: "FEATURE_USAGE",
        DATA_OPERATION: "DATA_OPERATION",
        ERROR: "ERROR",
        AUTH_EVENT: "AUTH_EVENT",
        USER_INTERACTION: "USER_INTERACTION",
        NAVIGATION: "NAVIGATION",
        PERFORMANCE: "PERFORMANCE",
        SYSTEM: "SYSTEM",
});

// Initialize Firestore
const db = firebase.firestore();

// Session management
let currentSessionId = null;

const safeNavigator = () => {
        try {
                if (typeof navigator !== "undefined") {
                        return navigator;
                }
        } catch (error) {
                console.warn("Navigator unavailable for device info:", error);
        }
        return {};
};

const safeWindow = () => {
        try {
                if (typeof window !== "undefined") {
                        return window;
                }
        } catch (error) {
                console.warn("Window unavailable for device info:", error);
        }
        return {};
};

/**
 * Initialize a new session for the user
 * @param {string} userId - The unique identifier for the user
 * @returns {string} The generated session ID
 */
export const initSession = (userId) => {
        currentSessionId = uuidv4();

        if (userId) {
                logActivity(userId, ActivityEventTypes.SESSION_START, {}, {
                        severity: ActivitySeverity.INFO,
                        tags: ["session"],
                }).finally(() => {
                        flushOfflineActivityLog(userId);
                });
        }

        return currentSessionId;
};

/**
 * Get current session or create new one
 * @param {string} userId - The unique identifier for the user
 * @returns {string} The current session ID
 */
export const getSessionId = (userId) => {
	if (!currentSessionId) {
		return initSession(userId);
	}
	return currentSessionId;
};

/**
 * Get device information
 * @returns {Object} Device information
 */
const getDeviceInfo = () => {
        const nav = safeNavigator();
        const win = safeWindow();

        return {
                deviceType: typeof getDeviceType === "function" ? getDeviceType() : "Unknown",
                browser: typeof getBrowserInfo === "function" ? getBrowserInfo() : "Unknown",
                os: nav?.platform ?? "Unknown",
                screenSize:
                        typeof getScreenSize === "function"
                                ? getScreenSize()
                                : win?.innerWidth
                                        ? `${win.innerWidth}px`
                                        : "Unknown",
        };
};

const buildActivityPayload = (
        userId,
        actionType,
        actionDetails,
        { severity = ActivitySeverity.INFO, tags = [], context = {}, metadata = {}, source = "application" } = {}
) => {
        const timestamp = new Date();

        return {
                userId,
                timestamp,
                actionType,
                actionDetails,
                sessionId: getSessionId(userId),
                deviceInfo: getDeviceInfo(),
                severity,
                tags,
                context,
                metadata,
                source,
                appVersion: process.env.REACT_APP_BUILD_VERSION || "unknown",
                brandId: brandConfig.id,
                productProfile: brandConfig.productProfile,
        };
};

const persistOfflineActivity = (activityData) => {
        if (typeof localStorage === "undefined") return;

        try {
                const raw = localStorage.getItem(OFFLINE_ACTIVITY_KEY);
                const queued = raw ? JSON.parse(raw) : [];

                queued.push({
                        ...activityData,
                        timestamp: activityData.timestamp.toISOString(),
                });

                while (queued.length > OFFLINE_QUEUE_LIMIT) {
                        queued.shift();
                }

                localStorage.setItem(OFFLINE_ACTIVITY_KEY, JSON.stringify(queued));
        } catch (error) {
                console.warn("Failed to persist offline activity:", error);
        }
};

const writeActivityToFirestore = async (userId, activityData) => {
        await db
                .collection("Users")
                .doc(userId)
                .collection("Activity")
                .add(activityData);
};

const writeGlobalActivityToFirestore = async (activityData) => {
        await db.collection("UserActivity").add(activityData);
};

const saveActivity = async (
        userId,
        activityData,
        { skipGlobalFallback = false, skipOfflineQueue = false } = {}
) => {
        try {
                await writeActivityToFirestore(userId, activityData);
        } catch (userActivityError) {
                if (skipGlobalFallback) {
                        throw userActivityError;
                }

                try {
                        await writeGlobalActivityToFirestore(activityData);
                } catch (globalActivityError) {
                        if (!skipOfflineQueue) {
                                persistOfflineActivity(activityData);
                        }

                        throw globalActivityError;
                }
        }
};

export const flushOfflineActivityLog = async (userId) => {
        if (!userId || typeof localStorage === "undefined") {
                return { flushed: 0, pending: 0, errors: [] };
        }

        const raw = localStorage.getItem(OFFLINE_ACTIVITY_KEY);
        if (!raw) {
                return { flushed: 0, pending: 0, errors: [] };
        }

        const queued = JSON.parse(raw);
        let flushed = 0;
        const errors = [];
        const remaining = [];

        for (const entry of queued) {
                try {
                        const hydrated = {
                                ...entry,
                                timestamp: entry.timestamp ? new Date(entry.timestamp) : new Date(),
                        };
                        await saveActivity(userId, hydrated, { skipOfflineQueue: true });
                        flushed += 1;
                } catch (error) {
                        errors.push(error.message);
                        remaining.push(entry);
                }
        }

        if (remaining.length > 0) {
                localStorage.setItem(OFFLINE_ACTIVITY_KEY, JSON.stringify(remaining));
        } else {
                localStorage.removeItem(OFFLINE_ACTIVITY_KEY);
        }

        return { flushed, pending: remaining.length, errors };
};

/**
 * Log user activity to Firestore
 * @param {string} userId - The unique identifier for the user
 * @param {string} actionType - The type of action (PAGE_VIEW, FEATURE_USAGE, etc.)
 * @param {Object} actionDetails - Details about the action
 * @returns {Promise<void>}
 */
const logActivity = async (userId, actionType, actionDetails = {}, options = {}) => {
        if (!userId) return;

        try {
                const activityData = buildActivityPayload(
                        userId,
                        actionType,
                        actionDetails,
                        options
                );

                try {
                        await saveActivity(userId, activityData);
                } catch (error) {
                        console.warn(
                                "Activity tracking fell back to offline queue:",
                                error.message
                        );
                }
        } catch (error) {
                console.error("Error logging activity:", error);
        }
};

/**
 * Log page view
 * @param {string} userId - The unique identifier for the user
 * @param {string} page - The page path
 * @returns {Promise<void>}
 */
export const logPageView = (userId, page, options = {}) => {
        return logActivity(
                userId,
                ActivityEventTypes.PAGE_VIEW,
                { page },
                { ...options, tags: [...(options.tags || []), "navigation"] }
        );
};

/**
 * Log feature usage
 * @param {string} userId - The unique identifier for the user
 * @param {string} component - The component name
 * @param {string} action - The action performed
 * @param {string|null} itemId - The ID of the item being acted upon
 * @param {Object} additionalData - Additional data about the action
 * @returns {Promise<void>}
 */
export const logFeatureUsage = (
        userId,
        component,
        action,
        itemId = null,
        additionalData = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.FEATURE_USAGE,
                {
                        component,
                        action,
                        itemId,
                        additionalData,
                },
                { ...options, tags: [...(options.tags || []), "feature"] }
        );
};

/**
 * Log data operation
 * @param {string} userId - The unique identifier for the user
 * @param {string} operation - The operation type (READ, WRITE, UPDATE, DELETE)
 * @param {string} collection - The collection name
 * @param {string} documentId - The document ID
 * @param {string} status - The operation status (SUCCESS, ERROR)
 * @returns {Promise<void>}
 */
export const logDataOperation = (
        userId,
        operation,
        collection,
        documentId,
        status,
        metadata = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.DATA_OPERATION,
                {
                        operation,
                        collection,
                        documentId,
                        status,
                        metadata,
                },
                { ...options, tags: [...(options.tags || []), "data"] }
        );
};

/**
 * Log error
 * @param {string} userId - The unique identifier for the user
 * @param {string} errorType - The type of error
 * @param {string} errorMessage - The error message
 * @param {string} component - The component where the error occurred
 * @returns {Promise<void>}
 */
export const logError = (
        userId,
        errorType,
        errorMessage,
        component,
        additionalData = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.ERROR,
                {
                        errorType,
                        errorMessage,
                        component,
                        additionalData,
                },
                {
                        ...options,
                        severity: options.severity || ActivitySeverity.ERROR,
                        tags: [...(options.tags || []), "error"],
                }
        );
};

/**
 * Log authentication event
 * @param {string} userId - The unique identifier for the user
 * @param {string} eventType - The type of event (LOGIN, LOGOUT, etc.)
 * @param {string} method - The authentication method
 * @param {string} status - The event status (SUCCESS, ERROR)
 * @returns {Promise<void>}
 */
export const logAuthEvent = (
        userId,
        eventType,
        method,
        status,
        additionalData = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.AUTH_EVENT,
                {
                        eventType,
                        method,
                        status,
                        additionalData,
                },
                { ...options, tags: [...(options.tags || []), "auth"] }
        );
};

export const logNavigationEvent = (
        userId,
        from,
        to,
        additionalData = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.NAVIGATION,
                {
                        from,
                        to,
                        additionalData,
                },
                { ...options, tags: [...(options.tags || []), "navigation"] }
        );
};

export const logInteractionEvent = (
        userId,
        {
                component,
                element,
                action,
                label = null,
                value = null,
                metadata = {},
        },
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.USER_INTERACTION,
                {
                        component,
                        element,
                        action,
                        label,
                        value,
                        metadata,
                },
                { ...options, tags: [...(options.tags || []), "interaction"] }
        );
};

export const logSystemEvent = (
        userId,
        event,
        severity = ActivitySeverity.INFO,
        metadata = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.SYSTEM,
                {
                        event,
                        metadata,
                },
                { ...options, severity, tags: [...(options.tags || []), "system"] }
        );
};

export const logPerformanceMetric = (
        userId,
        metric,
        value,
        metadata = {},
        options = {}
) => {
        return logActivity(
                userId,
                ActivityEventTypes.PERFORMANCE,
                {
                        metric,
                        value,
                        metadata,
                },
                { ...options, tags: [...(options.tags || []), "performance"] }
        );
};

/**
 * End the current session
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<void>}
 */
export const endSession = async (userId) => {
        if (!userId || !currentSessionId) return;

        try {
                await logActivity(userId, ActivityEventTypes.SESSION_END, {}, {
                        severity: ActivitySeverity.INFO,
                        tags: ["session"],
                });
                currentSessionId = null;
        } catch (error) {
                console.error("Error ending session:", error);
        }
};

export { logActivity };
